import { Component, computed, effect, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { showRequestErrors } from '@softline/application';
import { DateService, isDefined, Store } from '@softline/core';
import { Stable } from '../../types/stable.model';
import { WeightReport } from '../../types/weight-report.model';

import { MessageBarStore, SOFTLINE_FEATURE_MESSAGE_BAR, Validators, RadioGroupDirective, RadioOptionComponent } from '@softline/ui-core';
import { DefaultValuePayloadService } from '../../services/default-value-payload.service';
import { DefaultValueService } from '../../services/default-value.service';
import { ReportService } from '../../services/report.service';
import { StableService } from '../../services/stable.service';

import { FieldValidationDirective } from '@softline/ui-core';
import { FormatPipe } from '@softline/ui-core';
import { InputComponent } from '@softline/ui-core';
import { MultilineInputComponent } from '@softline/ui-core';
import { NumberInputComponent } from '@softline/ui-core';
import { SelectComponent, SelectOptionDirective, SelectOptionHeaderDirective, SelectOptionSeparatorDirective } from '@softline/ui-core/combo-box';
import { TranslatePipe } from '@softline/ui-core';
import { ValidationContainerComponent } from '@softline/ui-core';
import { ValidationMessagePipe } from '@softline/ui-core';
@Component({
  selector: 'lib-report-form',
  templateUrl: './report-form.component.html',
  styleUrls: ['./report-form.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    SelectComponent,
    SelectOptionDirective,
    FieldValidationDirective,
    FormatPipe,
    InputComponent,
    MultilineInputComponent,
    NumberInputComponent,
    RadioGroupDirective,
    RadioOptionComponent,
    TranslatePipe,
    ValidationContainerComponent,
    ValidationMessagePipe] })
export class ReportFormComponent implements OnInit {
  private readonly stableService = inject(StableService);
  private readonly defaultValueService = inject(DefaultValueService);
  private readonly defaultValuePayloadService = inject(DefaultValuePayloadService);
  private readonly weightReportService = inject(ReportService);

  form = this.fb.group({
    stable: [null, Validators.required()],
    quantity: [null],
    weight: [null, [Validators.min(0), Validators.max(9999999999)]],
    loss: [null, [Validators.min(0), Validators.max(100)]],
    info: [null],
    setDate: [null],
    day: [null],
    salmonellae: [false] });

  stables = this.stableService.value;
  templates = computed(() => this.defaultValueService.value());
  loadingDefault = computed(() => !this.defaultValueService.isLoading());

  readonly templatesEffect = effect(() => {
    const templates = this.templates();
    if (isDefined(templates)) {
      this.form.patchValue(templates as any);
    }
  });

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private dateService: DateService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadDefault();
  }

  async onStableChange(stable: Stable) {
    try {
      this.defaultValuePayloadService.stable.set(stable);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async onSubmit(value: WeightReport): Promise<void> {
    try {
      value = {
        ...value,
        date: this.dateService.now() };

      await this.weightReportService.create(value, undefined);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: '#MESSAGES.ADD_SUCCESS_TITLE',
          message: '#MESSAGES.ADD_SUCCESS_MESSAGE' }
      );

      this.form.reset();
      await this.loadDefault();
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  private async loadDefault(): Promise<void> {
    try {
      this.defaultValuePayloadService.stable.set(undefined);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
